import axios from 'axios';
import { Card, CardContent, Typography } from '@mui/material';
import { useDynamicWidget } from '@/hooks/dashboard/useDynamicWidget';
import { DashboardWidgetRenderer } from './DashboardWidgetRenderer';
import type { WidgetConfig } from '@/hooks/dashboard/useDashboardWidgets';
import type { ScopeFilterValue } from './ScopeFilterBar';

interface DynamicDashboardWidgetProps {
  widget: WidgetConfig;
  filters: {
    timeRangeKey: string;
    resolution: string;
    scope: ScopeFilterValue;
  };
}

/**
 * Wrapper component for non-DSL widgets that need dynamic data fetching.
 * Calls useDynamicWidget internally and passes data to DashboardWidgetRenderer.
 */
export function DynamicDashboardWidget({ widget, filters }: DynamicDashboardWidgetProps) {
  // Filter out sentinel values like 'current' that aren't valid GUIDs
  const legalEntityId = filters.scope.legalEntityId && filters.scope.legalEntityId !== 'current'
    ? filters.scope.legalEntityId : null;
  const businessUnitId = filters.scope.businessUnitId ?? null;

  const query = useDynamicWidget(widget, {
    timeRangeKey: filters.timeRangeKey,
    resolution: filters.resolution,
    legalEntityId,
    businessUnitId,
  });

  if (query.isError) {
    const err = query.error;
    let detail = 'Unknown error';
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const msg = err.response?.data?.error?.message ?? err.response?.data?.message ?? err.message;
      detail = `${status ?? 'Network'}: ${msg}`;
    } else if (err instanceof Error) {
      detail = err.message;
    }

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" noWrap>
            {widget.displayName}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Failed to load data
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', wordBreak: 'break-all' }}>
            {detail}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <DashboardWidgetRenderer
      widget={widget}
      data={query.data}
      loading={query.isLoading}
    />
  );
}
